import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Save, Trash2, Plus, X, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { SECTORS, sectorLabel } from "@/data/locations";

export default function MyBusiness() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [form, setForm] = useState(null);
  const [competitors, setCompetitors] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${id}`).then((r) => {
      const b = r.data;
      setForm(b);
      setCompetitors(b.competitors?.length ? b.competitors : [""]);
      setLoading(false);
    });
  }, [id]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/businesses/${id}`, {
        ...form,
        competitors: competitors.map((c) => c.trim()).filter(Boolean),
      });
      toast.success(t("mb.updatedToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("mb.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("mb.deleteConfirm"))) return;
    setDeleting(true);
    try {
      await api.delete(`/businesses/${id}`);
      toast.success(t("mb.deleteSuccess"));
      navigate("/app/onboarding");
    } catch (err) {
      toast.error(t("mb.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !form) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="my-business-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">
          <Building2 size={12} /> {t("mb.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{t("mb.title")}</h1>
      </div>

      <form onSubmit={save} className="glass-strong rounded-3xl p-8 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={t("onb.name")}>
            <input className="ueinput" required value={form.name || ""} onChange={(e) => change("name", e.target.value)} data-testid="mb-name" />
          </Field>
          <Field label={t("onb.sector")}>
            <select className="ueinput" required value={form.sector || ""} onChange={(e) => change("sector", e.target.value)} data-testid="mb-sector">
              <option value="">{t("onb.select")}</option>
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{sectorLabel(s.value, lang)}</option>)}
            </select>
          </Field>
          <Field label={t("onb.location")}>
            <input className="ueinput" required value={form.city || ""} onChange={(e) => change("city", e.target.value)} data-testid="mb-city" />
          </Field>
          <Field label={t("onb.website")}>
            <input className="ueinput" type="url" value={form.website_url || ""} onChange={(e) => change("website_url", e.target.value)} data-testid="mb-website" />
          </Field>
          <Field label={t("onb.maps")}>
            <input className="ueinput" type="url" value={form.google_maps_url || ""} onChange={(e) => change("google_maps_url", e.target.value)} data-testid="mb-maps" />
          </Field>
          <Field label={t("mb.targetCustomer")}>
            <input className="ueinput" value={form.target_customer || ""} onChange={(e) => change("target_customer", e.target.value)} data-testid="mb-target" />
          </Field>
        </div>
        <Field label={t("onb.desc")}>
          <textarea className="ueinput min-h-[80px]" value={form.description || ""} onChange={(e) => change("description", e.target.value)} data-testid="mb-desc" />
        </Field>
        <Field label={t("onb.services")}>
          <textarea className="ueinput min-h-[80px]" value={form.services || ""} onChange={(e) => change("services", e.target.value)} data-testid="mb-services" />
        </Field>
        <Field label={t("onb.targetQuestions")}>
          <textarea
            className="ueinput min-h-[80px]"
            value={form.target_ai_questions || ""}
            onChange={(e) => change("target_ai_questions", e.target.value)}
            placeholder={t("onb.targetQuestionsPh")}
            data-testid="mb-target-questions"
          />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-slate-400 uppercase tracking-wider">{t("onb.competitors")}</label>
            {competitors.length < 3 && (
              <button type="button" onClick={() => setCompetitors([...competitors, ""])} className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                <Plus size={12} /> {t("common.add")}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {competitors.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="ueinput flex-1"
                  type="url"
                  value={c}
                  onChange={(e) => {
                    const arr = [...competitors];
                    arr[i] = e.target.value;
                    setCompetitors(arr);
                  }}
                  placeholder={t("onb.competitorPh")}
                  data-testid={`mb-competitor-${i}`}
                />
                {competitors.length > 1 && (
                  <button type="button" onClick={() => setCompetitors(competitors.filter((_, j) => j !== i))} className="ghost-btn rounded-lg px-3"><X size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-white/5">
          <button
            type="button"
            onClick={remove}
            disabled={deleting}
            className="rounded-xl px-5 py-2.5 text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-2"
            data-testid="mb-delete"
          >
            {deleting && <Loader2 size={14} className="animate-spin" />}
            <Trash2 size={14} /> {t("mb.deleteBtn")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="neon-btn rounded-xl px-6 py-2.5 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="mb-save"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            <Save size={14} /> {t("common.save")}
          </button>
        </div>
      </form>

      <style>{`
        .ueinput {
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 0.6rem;
          padding: 0.65rem 0.9rem;
          color: #fff;
          font-size: 0.95rem;
        }
        .ueinput:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
        .ueinput::placeholder { color: rgba(148,163,184,0.5); }
        select.ueinput option { background-color: #0f1218; color: #e2e8f0; }
        select.ueinput option:disabled { color: rgba(148,163,184,0.6); }
        select.ueinput option:checked { background-color: #1e293b; color: #fff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider block mb-2">{label}</label>
      {children}
    </div>
  );
}
